import React from "react";
import { useCustomerPolicyDetails, useDispatchWrapper } from "../../../../../../hooks";
import { useFormik } from "formik";
import { Grid, MenuItem } from "@mui/material";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomSelect,
  CustomTextField,
} from "../../../../../../components/formsComponents";
import { FileUpload } from "../../../../../../features/FileUpload/components/FileUpload";
import { isAlphaNumeric, renderDropdownValue } from "../../../../../../helpers";
import { Dialog } from "../../../../../../components/Dialogs/Dialog";
import { UsersAutoSearchByRole } from "../../../../../../components/AutoCompleteSearches/UsersAutoSearchByRole";
import { ICommAutoGaragePlatesDialogProps } from "./CommAutoGaragePlate.types";
import { addCommAutomobileGaragePlatesAsync } from "../../../../../../redux/commAutoPolicy/commAutoGaragePlates/commAutoGaragePlatesActions";
import { useCurrentUserId } from "../../../../../../hooks/useAssignedTo";
import { COMMON_STATUS } from "../../../../../../constants/constants";

export const CommAutoGaragePlatesDialog: React.FC<
  ICommAutoGaragePlatesDialogProps
> = (props) => {
  const { data, open, leadId } = props;

  const [saveLoading, setSaveLoading] = React.useState(false);
  const dispatch = useDispatchWrapper();

  const currentUserId = useCurrentUserId();

  // In customer-policy scope the customer id is derived from the saved policy.
  // In lead scope no policy exists, so `customer_policy_id` is null and this
  // hook dispatches no fetch; the lead's customer id is provided via props
  // instead.
  const { details: policyAdmin } = useCustomerPolicyDetails(
    data.customer_policy_id ?? undefined
  );

  const resolvedCustomerId = leadId
    ? props.customerId
    : policyAdmin?.data?.customer_id;

  const [file, setFile] = React.useState<any>(null);

  const { values, handleChange, handleSubmit, setFieldValue, errors } =
    useFormik({
      initialValues: data,
      validate: (values) => {
        const errors: any = {};
        if (values.plate_number && !isAlphaNumeric(values.plate_number)) {
          errors.plate_number = "Must alpha numeric.";
        }
        return errors;
      },
      onSubmit: (values) => {
        setSaveLoading(true);
        dispatch(
          addCommAutomobileGaragePlatesAsync(
            {
              ...values,
            },
            resolvedCustomerId,
            file,
            (isSuccess) => {
              if (isSuccess) {
                window.scrollTo(0, 0);
                props.onComplete();
              }
              setSaveLoading(false);
            },
            // Lead scope: persist keyed by `lead_id` (customer_policy_id
            // omitted). Undefined in customer-policy scope, preserving the
            // existing body shape byte-for-byte.
            leadId
          )
        );
      },
    });

  React.useEffect(() => {
    setFieldValue("assigned_to", currentUserId);
  }, [currentUserId]);

  return (
    <Dialog
      open={open}
      title="Create/Edit Garage Plate"
      onClose={props.onClose}
      size="md"
      contentWrappedWithForm={{ onSubmit: handleSubmit }}
      actions={[
        {
          type: "button",
          label: "Close",
          variant: "text",
          onClick: props.onClose,
        },
        {
          type: "submit",
          label: "Save",
          variant: "contained",
          disabled: saveLoading,
        },
      ]}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <CustomFormLabel>#</CustomFormLabel>
          <CustomTextField
            name="serial_number"
            variant="outlined"
            size="small"
            type="text"
            fullWidth
            value={values.serial_number}
            disabled
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <CustomFormLabel>Plate #</CustomFormLabel>
          <CustomTextField
            name="plate_number"
            variant="outlined"
            size="small"
            type="string"
            fullWidth
            value={values.plate_number}
            error={errors.plate_number ? true : false}
            helperText={errors.plate_number}
            onChange={(e) => {
              setFieldValue("plate_number", e.target.value.toUpperCase());
            }}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <CustomFormLabel>Type</CustomFormLabel>
          <CustomSelect
            variant="outlined"
            size="small"
            type="string"
            name="type"
            value={values.type}
            onChange={handleChange}
            displayEmpty
            fullWidth
            renderValue={renderDropdownValue(`Select one`)}
          >
            {["Service Plate", "Dealer Plate", "White Plate"].map(
              (policyType) => {
                return <MenuItem value={policyType}>{policyType}</MenuItem>;
              }
            )}
          </CustomSelect>
        </Grid>

        <Grid item xs={12} lg={3}>
          <UsersAutoSearchByRole
            label="Created By"
            //roleId={USER_ROLES.CSR}
            value={values.assigned_to}
            onSelect={(newValue) => {
              setFieldValue("assigned_to", newValue.user_fact_id);
            }}
          />
        </Grid>

        <Grid item xs={12} md={9}>
          <CustomFormLabel>Attachment</CustomFormLabel>
          <FileUpload
            value={values.attachment}
            onChange={(file) => setFile(file)}
          />
        </Grid>
        <Grid item xs={12} lg={3}>
          <CustomFormLabel>Status</CustomFormLabel>
          <ControlledCustomSelect
            fullWidth
            value={values.status}
            name="status"
            onChange={handleChange}
            placeholder="Select one"
            displayEmpty
            options={COMMON_STATUS}
          />
        </Grid>
      </Grid>
    </Dialog>
  );
};
